import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Container,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    Link,
} from '@mui/material';
import { PlayArrow, Settings, Lock, Info } from '@mui/icons-material';
// import AsteroidsGameWrapper from './AsteroidsGameWrapper';
import PacmanGameWrapper from './games/pacman-2D/PacmanGameWrapper';
import PacmanPhysicsGameWrapper from './games/pacman-Physics/PacmanGameWrapper';
import PacmanAnimationsGameWrapper from './games/pacman-Animations/PacmanGameWrapper';
import PacmanSaharaGameWrapper from './games/pacman-Sahara/PacmanGameWrapper';
import PacmanAmazonGameWrapper from './games/pacman-Amazon/PacmanGameWrapper';
import PacmanAtlanticGameWrapper from './games/pacman-Atlantic/PacmanGameWrapper';
import PacmanAntarcticaGameWrapper from './games/pacman-Antartica/PacmanGameWrapper';
import PacmanSpaceGameWrapper from './games/pacman-Space/PacmanGameWrapper';
import PacmanMuddyGameWrapper from './games/pacman-Muddy/PacmanGameWrapper';
import { WORLDS_ORDER, getUnlockedCount, unlockNextAfter } from './utils/progression';

interface GameInfo {
    id: string;
    title: string;
    description: string;
    icon: string;
    image?: string;
    component: React.ComponentType<any>;
    width?: number;
    height?: number;
}

const games: GameInfo[] = [
    {
        id: 'pacman-2d',
        title: 'Classic Dotman',
        description: 'Classic maze: collect dots, avoid ghosts.',
        icon: '🟡',
        image: '/GameAssets/images/dotman-classic.png',
        component: PacmanGameWrapper,
        width: 672,
        height: 744,
    },
    {
        id: 'pacman-physics-game',
        title: 'Bricks Dotman',
        description: 'Mud brick maze with physics effects.',
        icon: '🧱',
        image: '/GameAssets/images/mud.jpg',
        component: PacmanPhysicsGameWrapper,
        width: 800,
        height: 600,
    },
    {
        id: 'pacman-animations-game',
        title: 'Snowy Dotman',
        description: 'Snowy maze with animated effects.',
        icon: '❄️',
        image: '/GameAssets/images/snow.jpg',
        component: PacmanAnimationsGameWrapper,
        width: 800,
        height: 600,
    },
    {
        id: 'pacman-sahara',
        title: 'Sahara Dotman',
        description: 'Desert adventure with sandstone walls, blowing sand, and erosion effects.',
        icon: '🐪',
        image: '/GameAssets/images/desert.jpg',
        component: PacmanSaharaGameWrapper,
        width: 800,
        height: 600,
    },
    {
        id: 'pacman-amazon',
        title: 'Amazon Dotman',
        description: 'Explore the wild Amazon jungle.',
        icon: '🌴',
        image: '/GameAssets/images/jungle.jpg',
        component: PacmanAmazonGameWrapper,
        width: 800,
        height: 600,
    },

    {
        id: 'pacman-atlantic',
        title: 'Atlantic Dotman',
        description: 'Dive into the deep Atlantic ocean.',
        icon: '🌊',
        image: '/GameAssets/images/iland.jpg',
        component: PacmanAtlanticGameWrapper,
        width: 800,
        height: 600,
    },

    {
        id: 'pacman-antarctica',
        title: 'Antarctica Dotman',
        description: 'Frozen maze with ice walls, snowstorms, and glacial effects.',
        icon: '🧊',
        image: '/GameAssets/images/iceberg2.webp',
        component: PacmanAntarcticaGameWrapper,
        width: 800,
        height: 600,
    },
    {
        id: 'pacman-space',
        title: 'Space Dotman',
        description: 'Cosmic maze with floating debris, stardust, and space effects.',
        icon: '🚀',
        image: '/GameAssets/images/space2.jpg',
        component: PacmanSpaceGameWrapper,
        width: 800,
        height: 600,
    },
    {
        id: 'pacman-muddy',
        title: 'Muddy Dotman',
        description: 'Mysterious muddy maze with bricks, earth and mud effects.',
        icon: '🧱',
        image: '/GameAssets/images/mud2.avif',
        component: PacmanMuddyGameWrapper,
        width: 800,
        height: 600,
    },
];

interface GameLauncherProps {
    onBackToLauncher: () => void;
}

const GameLauncher: React.FC<GameLauncherProps> = ({ onBackToLauncher }) => {
    const [selectedGame, setSelectedGame] = useState<GameInfo | null>(null);
    const [isGameRunning, setIsGameRunning] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [unlockedCount, setUnlockedCount] = useState<number>(() => getUnlockedCount());
    const [mazeMode, setMazeMode] = useState<'fixed' | 'random'>(() =>
        (localStorage.getItem('mazeMode') as 'fixed' | 'random') || 'fixed'
    );
    // Generate a NEW random seed on every page load for Mystic World (don't persist seed)
    const [mazeSeed] = useState<string>(() => {
        // Always generate fresh seed - each reload = new random maze
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}-${Math.random().toString(36).slice(2, 6)}`;
    });
    const orderedGames = useMemo(() => {
        // Ensure ordering matches WORLDS_ORDER
        const map = new Map(games.map(g => [g.id, g] as const));
        return WORLDS_ORDER.map(id => map.get(id)!).filter(Boolean);
    }, []);

    // Persist only the mode preference (not the seed)
    useEffect(() => {
        try { localStorage.setItem('mazeMode', mazeMode); } catch { }
    }, [mazeMode]);

    const handleGameSelect = (game: GameInfo) => {
        setSelectedGame(game);
        setShowSettings(true);
    };

    const handleStartGame = () => {
        if (selectedGame) {
            setIsGameRunning(true);
            setShowSettings(false);
        }
    };

    const handleCloseGame = () => {
        setIsGameRunning(false);
        setSelectedGame(null);
    };

    const handleBackToLauncher = () => {
        handleCloseGame();
        onBackToLauncher();
    };

    if (isGameRunning && selectedGame) {
        const GameComponent = selectedGame.component;
        return (
            <Box sx={{ width: '100vw', height: '100vh', position: 'relative' }}>

                <Box sx={{ width: '100%', height: '100%' }}>
                    <GameComponent
                        width={selectedGame.width || 800}
                        height={selectedGame.height || 600}
                        mazeMode={mazeMode}
                        mazeSeed={mazeSeed}
                        displayLevelStart={(() => {
                            const idx = WORLDS_ORDER.indexOf(selectedGame.id as any);
                            return idx >= 0 ? idx + 1 : 1;
                        })()}
                        onLevelComplete={() => {
                            const next = unlockNextAfter(selectedGame.id as any);
                            setUnlockedCount(next);
                            // Auto-advance to next world
                            const currentIndex = WORLDS_ORDER.indexOf(selectedGame.id as any);
                            const nextIndex = currentIndex + 1;
                            if (nextIndex < WORLDS_ORDER.length) {
                                const map = new Map(games.map(g => [g.id, g] as const));
                                const nextGame = map.get(WORLDS_ORDER[nextIndex]);
                                if (nextGame) {
                                    // Switch to next world immediately
                                    setSelectedGame(nextGame);
                                    setIsGameRunning(true);
                                    setShowSettings(false);
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100vh', // Fixed height to viewport
                background: 'linear-gradient(135deg,rgb(10, 12, 22) 0%,rgb(44, 26, 61) 100%)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden', // Prevent body scroll
            }}
        >
            {/* Header Section - Fixed */}
            <Box sx={{
                py: { xs: 5, sm: 6, md: 3 },
                px: { xs: 1.5, sm: 2 },
                flexShrink: 0, // Don't shrink
                textAlign: 'center',
                transform: { xs: 'scale(0.65)', sm: 'scale(0.75)', md: 'scale(1)' },
                transformOrigin: 'top center',
            }}>
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        mb: { xs: 1, sm: 1.5, md: 2 },
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        lineHeight: { xs: 1.15, sm: 1.2, md: 1.2 },
                        whiteSpace: 'nowrap'
                    }}
                >
                    Dotman Worlds
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: 'rgba(255,255,255,0.9)',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                        mb: { xs: 0.5, sm: 0.75, md: 1 },
                        lineHeight: { xs: 1.25, sm: 1.3, md: 1.35 }
                    }}
                >
                    Start with the Classic. Beat a world to unlock the next.
                </Typography>

                {/* Fixed vs Random World selector and About - Inline */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, sm: 2 },
                    mt: { xs: 0.5, sm: 1 }
                }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={mazeMode === 'fixed'}
                                onChange={() => setMazeMode('fixed')}
                                sx={{ color: '#9EDCE8', '&.Mui-checked': { color: '#00E5FF' } }}
                            />
                        }
                        label={<Typography variant="body2" sx={{ color: '#DFFBFF', fontWeight: 700 }}>Basic World</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={mazeMode === 'random'}
                                onChange={() => setMazeMode('random')}
                                sx={{ color: '#9EDCE8', '&.Mui-checked': { color: '#00E5FF' } }}
                            />
                        }
                        label={<Typography variant="body2" sx={{ color: '#DFFBFF', fontWeight: 700 }}>Mystic World</Typography>}
                    />
                    <Button
                        onClick={() => setShowAbout(true)}
                        sx={{
                            color: '#CCF7FF',
                            fontWeight: 700,
                            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                            textTransform: 'none',
                            px: { xs: 1, sm: 1.5 },
                            py: { xs: 0.5, sm: 0.75 },
                            minWidth: 'auto',
                            '&:hover': {
                                color: '#00E5FF',
                                backgroundColor: 'rgba(0,229,255,0.1)',
                            },
                        }}
                        startIcon={<Info fontSize="small" />}
                    >
                        About
                    </Button>
                </Box>
            </Box>

            {/* Scrollable Cards Container */}
            <Box sx={{
                flex: 1, // Take remaining space
                overflow: 'auto', // Enable scrolling
                mt: { xs: -8, sm: -10, md: 0 },
                px: 1,
                pb: 2,
                pt: 2,
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                        background: 'rgba(255,255,255,0.5)',
                    },
                },
            }}>
                <Container maxWidth="lg" disableGutters>
                    <Grid
                        container
                        rowSpacing={3}
                        columnSpacing={0}
                        sx={{
                            width: '100%',
                            maxWidth: 520,
                            mx: 'auto',
                            transform: { xs: 'scale(0.8)', sm: 'scale(0.9)', md: 'scale(1)' },
                            transformOrigin: 'top center',
                            '& > *': {
                                transform: 'scale(1)', // Prevent double scaling of child elements
                            }
                        }}
                    >
                        {orderedGames.map((game, index) => {
                            const isUnlocked = index < unlockedCount;
                            return (
                                <Grid item xs={4} sx={{ px: '1px' }} key={game.id}>
                                    <Card
                                        sx={{
                                            height: 150,
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: isUnlocked ? 'pointer' : 'not-allowed',
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'none',
                                                boxShadow: isUnlocked
                                                    ? `0 0 0 2px #00E5FF, 0 0 16px rgba(0,229,255,0.75), 0 0 28px rgba(0,229,255,0.45)`
                                                    : 'none',
                                            },
                                            background: 'linear-gradient(180deg, rgba(10,12,28,0.92), rgba(18,20,38,0.92))',
                                            borderRadius: '12px',
                                            border: '2px solid #00E5FF',
                                            boxShadow: `
                                            0 0 0 2px rgba(0,0,0,0.35) inset,
                                            0 0 10px rgba(0,229,255,0.35),
                                            0 0 20px rgba(0,229,255,0.25)
                                        `,
                                            overflow: 'hidden',
                                            position: 'relative',
                                        }}
                                        onClick={() => {
                                            if (!isUnlocked) return;
                                            handleGameSelect(game);
                                        }}
                                    >
                                        <CardMedia
                                            component="div"
                                            image={game.image}
                                            title={game.title}
                                            sx={{
                                                position: 'relative',
                                                pt: '100%', // 1:1 square
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                                            }}
                                        />
                                        <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 1.5, px: 1.5 }}>
                                            <Typography
                                                gutterBottom
                                                variant="h6"
                                                component="h2"
                                                sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.05rem' }}
                                            >
                                                {index + 1}. {game.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 1, lineHeight: 1.4, fontSize: '0.85rem' }}
                                            >
                                                {isUnlocked ? game.description : 'Beat the previous world to unlock.'}
                                            </Typography>
                                        </CardContent>
                                        {!isUnlocked && (
                                            <>
                                                {/* Top-right lock icon */}
                                                <Box sx={{ position: 'absolute', top: 6, right: 6, color: '#00E5FF' }}>
                                                    <Lock fontSize="small" />
                                                </Box>
                                                {/* Dim overlay with title */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.35))',
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                        justifyContent: 'center',
                                                        p: 1.5,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ color: '#CCF7FF', fontWeight: 700 }}>
                                                        {index + 1}. {game.title}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                        {isUnlocked && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(0deg, rgba(0,0,0,0.65), rgba(0,0,0,0.0))',
                                                    px: 1,
                                                    py: 0.5,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: '#CCF7FF', fontWeight: 700 }}>
                                                    {index + 1}. {game.title}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Container>
            </Box>

            {/* Settings Dialog - modern overlay */}
            <Dialog
                open={showSettings}
                onClose={() => setShowSettings(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(180deg, rgba(10,12,28,0.96), rgba(18,20,38,0.96))',
                        border: '2px solid #00E5FF',
                        boxShadow: `0 0 10px rgba(0,229,255,0.35), 0 0 24px rgba(0,229,255,0.25)`,
                        borderRadius: 2,
                        overflow: 'hidden',
                    },
                }}
                BackdropProps={{
                    sx: { backdropFilter: 'blur(4px)' },
                }}
            >
                <DialogTitle sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#CCF7FF' }}>
                        <Settings fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Ready for {selectedGame?.title}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                        {/* Preview image */}
                        <Box
                            sx={{
                                flex: { xs: '0 0 auto', sm: '0 0 120px' },
                                width: { xs: '100%', sm: 120 },
                                maxWidth: { xs: 200, sm: 'none' },
                                height: { xs: 0, sm: 120 },
                                minHeight: { xs: 0, sm: 0 },
                                pt: { xs: '50%', sm: 0 },
                                mx: { xs: 'auto', sm: 0 },
                                alignSelf: { xs: 'center', sm: 'flex-start' },
                                borderRadius: 1,
                                border: '1px solid rgba(0,229,255,0.4)',
                                backgroundImage: `url(${selectedGame?.image})`,
                                backgroundSize: { xs: 'contain', sm: 'cover' },
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundColor: { xs: 'rgba(0,0,0,0.25)', sm: 'transparent' },
                                overflow: 'hidden',
                            }}
                        />
                        {/* Info */}
                        <Box sx={{ flex: 1, color: '#DFFBFF', mt: { xs: 0.5, sm: 0 } }}>
                            <Typography variant="body2" sx={{ mb: 1.2 }}>
                                {selectedGame?.description}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ border: '1px solid #00E5FF', color: '#00E5FF', px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 700 }}>
                                    Level {(() => { const i = selectedGame ? WORLDS_ORDER.indexOf(selectedGame.id as any) : -1; return i >= 0 ? i + 1 : 1; })()}
                                </Box>
                                <Box sx={{ border: '1px solid #AAFF00', color: '#AAFF00', px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 700 }}>
                                    {(() => { const i = selectedGame ? WORLDS_ORDER.indexOf(selectedGame.id as any) : 0; return i <= 1 ? 'Easy' : i <= 3 ? 'Medium' : 'Hard'; })()}
                                </Box>
                                <Box sx={{ border: '1px solid #FFB300', color: '#FFB300', px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 700 }}>
                                    Music & SFX
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1.2 }}>
                                <Typography variant="caption" sx={{ color: '#9EDCE8' }}>
                                    - Goal: Collect all dots. Avoid ghosts. Power pellets stun ghosts briefly.
                                </Typography>
                                <br />
                                <Typography variant="caption" sx={{ color: '#9EDCE8' }}>
                                    - Controls: Arrow keys (move), P (pause), M (+/-) volume in some worlds.
                                </Typography>
                                <br />
                                <Typography variant="caption" sx={{ color: '#9EDCE8' }}>
                                    - Progression: Beat this world to unlock the next world.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setShowSettings(false)} sx={{ color: '#CCF7FF' }}>Cancel</Button>
                    <Button
                        onClick={handleStartGame}
                        variant="contained"
                        startIcon={<PlayArrow />}
                        sx={{
                            background: 'linear-gradient(90deg, #00E5FF 0%, #00B8D4 100%)',
                            color: '#001018',
                            fontWeight: 800,
                            boxShadow: '0 0 12px rgba(0,229,255,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #00F0FF 0%, #00CBE4 100%)',
                                boxShadow: '0 0 16px rgba(0,229,255,0.6)'
                            },
                        }}
                    >
                        Start Game
                    </Button>
                </DialogActions>
            </Dialog>

            {/* About Dialog */}
            <Dialog
                open={showAbout}
                onClose={() => setShowAbout(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(180deg, rgba(10,12,28,0.96), rgba(18,20,38,0.96))',
                        border: '2px solid #00E5FF',
                        boxShadow: `0 0 10px rgba(0,229,255,0.35), 0 0 24px rgba(0,229,255,0.25)`,
                        borderRadius: 2,
                        overflow: 'hidden',
                    },
                }}
                BackdropProps={{
                    sx: { backdropFilter: 'blur(4px)' },
                }}
            >
                <DialogTitle sx={{ px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#CCF7FF' }}>
                        <Info fontSize="medium" />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            About Dotman Worlds
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Box sx={{ color: '#DFFBFF', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Game Description */}
                        <Box>
                            <Typography variant="h6" sx={{ color: '#00E5FF', fontWeight: 800, mb: 1.5 }}>
                                Welcome to Dotman Worlds
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 1.5, color: '#CCF7FF' }}>
                                <strong style={{ color: '#00E5FF' }}>Developed by:</strong> Carlos Semeho Edorh
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 1.5 }}>
                                What started as an assignment in a game engine development course has grown into a fully-featured
                                2D maze-running game experience inspired by classic arcade maze games. Built with the <strong style={{ color: '#00E5FF' }}>Entity Component System (ECS)</strong> architecture,
                                this game demonstrates modern game development principles through engaging maze navigation gameplay.
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                                Journey through <strong style={{ color: '#AAFF00' }}>9 unique worlds</strong>, each with its own theme,
                                challenges, and visual style. From the classic maze to frozen Antarctica, from the Amazon jungle to
                                deep space, each world offers a fresh adventure while maintaining the core maze-running mechanics inspired by classic arcade games.
                            </Typography>
                        </Box>

                        {/* Features */}
                        <Box>
                            <Typography variant="h6" sx={{ color: '#00E5FF', fontWeight: 800, mb: 1.5 }}>
                                Features
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 3, lineHeight: 2 }}>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>9 Unique Worlds:</strong> Each with distinct themes, visuals, and atmospheres
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>Basic World:</strong> Classic fixed maze layouts for consistent gameplay
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>Mystic World:</strong> Random maze variants that generate on each browser reload
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>Progressive Unlocking:</strong> Beat a world to unlock the next challenge
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>Mobile-Friendly:</strong> Responsive design with touch controls for mobile devices
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>ECS Architecture:</strong> Clean, modular, and scalable codebase
                                </Typography>
                            </Box>
                        </Box>

                        {/* ECS Architecture */}
                        <Box>
                            <Typography variant="h6" sx={{ color: '#00E5FF', fontWeight: 800, mb: 1.5 }}>
                                Entity Component System Architecture
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 1 }}>
                                This game showcases the power of ECS architecture over traditional Object-Oriented Programming:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 3, lineHeight: 2 }}>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Composition over Inheritance:</strong> Flexible entity definitions through component composition
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Decoupled Systems:</strong> Independent systems that operate on entities with specific components
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Data-Oriented Design:</strong> Optimized for performance and cache efficiency
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Scalability:</strong> Easy to add new features, systems, and components
                                </Typography>
                            </Box>
                        </Box>

                        {/* Acknowledgments */}
                        <Box>
                            <Typography variant="h6" sx={{ color: '#00E5FF', fontWeight: 800, mb: 1.5 }}>
                                Acknowledgments
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 1 }}>
                                This project was created as part of a game engine development course, demonstrating the practical
                                application of ECS architecture principles. Special thanks to:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 3, lineHeight: 2 }}>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    <strong style={{ color: '#00E5FF' }}>Professor Juancho Buchanan</strong> (Professor of the Practice) for teaching game engine development and introducing ECS architecture concepts
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    The open-source community for inspiration and best practices
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Classic arcade maze games for providing the timeless gameplay foundation
                                </Typography>
                            </Box>
                        </Box>

                        {/* Technical Info */}
                        <Box sx={{
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid rgba(0,229,255,0.3)',
                            background: 'rgba(0,229,255,0.05)',
                        }}>
                            <Typography variant="caption" sx={{ color: '#9EDCE8', lineHeight: 1.8, display: 'block' }}>
                                <strong style={{ color: '#00E5FF' }}>Built with:</strong> React, TypeScript, Material-UI, Canvas API, and a custom 2D ECS engine
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9EDCE8', lineHeight: 1.8, display: 'block', mt: 1 }}>
                                <strong style={{ color: '#00E5FF' }}>Game Engine:</strong> Entity Component System (ECS) - A 2D game engine with Components, Systems, and Entities working together seamlessly
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9EDCE8', lineHeight: 1.8, display: 'block', mt: 1 }}>
                                <strong style={{ color: '#00E5FF' }}>Source Code:</strong> The engine is open-sourced on GitHub.
                                <Link
                                    href="https://github.com/edcarlos-92/dotman-ecs"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: '#00E5FF',
                                        ml: 0.5,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: '#00F0FF',
                                        }
                                    }}
                                >
                                    View repository
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowAbout(false)}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(90deg, #00E5FF 0%, #00B8D4 100%)',
                            color: '#001018',
                            fontWeight: 800,
                            boxShadow: '0 0 12px rgba(0,229,255,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #00F0FF 0%, #00CBE4 100%)',
                                boxShadow: '0 0 16px rgba(0,229,255,0.6)'
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GameLauncher; 